import { Link } from 'react-router-dom';
import { formatDate, formatTime, money } from '@busbook/ui';
import { useFeature, useFeatureLabel } from '../../lib/tenant.js';
import { useT } from '../../i18n/index.js';
import { EmailTicketAction } from './EmailTicket.js';
import type { BookingView } from '../Ticket.js';

/**
 * An upcoming trip: a journey the passenger still has to make, and therefore the only kind
 * that carries a usable ticket.
 *
 * Past trips are a different component on purpose (see PastTrips). They are a record
 * rather than a thing to act on, and a card that offered a ticket for a bus that left last
 * month would be offering a document nobody can use.
 */

export function TripCard({ booking, timezone }: { booking: BookingView; timezone: string }) {
  return (
    <li
      className="rounded-xl border"
      style={{ borderColor: 'var(--color-line)', background: 'var(--color-surface)' }}
    >
      <Link to={`/booking/${booking.reference}`} className="flex items-center gap-3 p-3">
        <TripSummary booking={booking} timezone={timezone} />
      </Link>
      <TripActions booking={booking} />
    </li>
  );
}

/** The same three lines whether or not the row is tappable. */
export function TripSummary({
  booking,
  timezone,
}: {
  booking: BookingView;
  timezone: string;
}) {
  return (
    <>
      <time className="board-time text-lg" dateTime={booking.trip.departureAt}>
        {formatTime(booking.trip.departureAt, timezone)}
      </time>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          {booking.trip.fromStopName} → {booking.trip.toStopName}
        </p>
        <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
          {formatDate(booking.trip.departureAt, timezone)} ·{' '}
          <span className="tabular">{booking.reference}</span>
        </p>
      </div>
      <span className="money text-sm font-semibold">
        {money(booking.totalCents, booking.currency)}
      </span>
    </>
  );
}

/**
 * What a passenger can do with an upcoming booking. Email and calendar are each gated on
 * the operator having that feature switched on.
 *
 * "View ticket" is the exception: not operator-switchable, and always shown. The card is
 * already a link to the ticket, but a link with no label is a secret — and the ticket is
 * the single thing most passengers open this app to find.
 */
function TripActions({ booking }: { booking: BookingView }) {
  const t = useT();
  const emailTicket = useFeature('emailTicket');
  const calendar = useFeature('calendar');
  const emailLabel = useFeatureLabel('emailTicket', t('m.emailTicketAction'));
  const calendarLabel = useFeatureLabel('calendar', t('m.addToCalendar'));

  return (
    <div
      className="flex flex-wrap items-center gap-2 border-t px-3 py-2"
      style={{ borderColor: 'var(--color-line)' }}
    >
      <Link className="trip-action" to={`/booking/${booking.reference}`}>
        {t('m.viewTicket')}
      </Link>
      {emailTicket && <EmailTicketAction booking={booking} label={emailLabel} />}
      {calendar && (
        <a className="trip-action" href={`/api/v1/public/bookings/${booking.reference}/calendar.ics`}>
          {calendarLabel}
        </a>
      )}
    </div>
  );
}

/**
 * Rebooking re-runs the search rather than copying the old trip forward. Fares,
 * availability and seats all have to be resolved at today's prices; anything that carried
 * the previous trip's numbers over would be quietly wrong.
 */
export function rebookSearch(booking: BookingView): string {
  const params = new URLSearchParams({
    from: booking.trip.fromStopName,
    to: booking.trip.toStopName,
    passengers: String(booking.seats?.length ?? 1),
  });
  return `/results?${params.toString()}`;
}
